/**
 * CatalogBrowser — the shared "discover → select" catalog picker.
 *
 * Replaces the old pick-and-jump grid (ToolSelectDialog) with a master-detail
 * flow that lets the user READ before committing:
 *
 *   1. **Grid (3×3):** tiles for each entry, with a search box + optional tabs.
 *   2. **Discover:** click a tile → the grid collapses to a left 1×N list and a
 *      right panel shows that entry's detail (markdown body, scrollable). Click
 *      another list item to switch; "← Back" returns to the grid.
 *   3. **Select (persistent footer):**
 *        - single-select  → "Select" commits the focused entry + closes.
 *        - multi-select   → a chip strip of chosen entries (add/remove, capped);
 *                           tiles toggle in/out; "Done" commits the set.
 *
 * Same interaction for MCP / connectors / skills; what "Select" *does* is the
 * caller's job (config wizard vs add-to-set) via ``onSelect`` / ``onDone``.
 * This is a NEW component — ToolSelectDialog is left untouched (live MCP +
 * connector flows) and migrates to this in a follow-up.
 *
 * Props:
 *   open            : boolean
 *   title, subtitle : header text
 *   entries         : [{ id, label, description, badge?, ...opaque }]
 *   tabs?           : [{ id, label }] — optional category tabs; entry.category matches
 *   loadDetail?     : async (entry) => { body?: string, ... }  (markdown body for the panel)
 *   multiSelect     : boolean
 *   selectedIds?    : string[]            (multi-select: current chosen ids)
 *   onSelect?       : (entry) => void     (single-select: commit + caller closes)
 *   onToggle?       : (entry, nowSelected) => void  (multi-select: add/remove one)
 *   onDone?         : () => void          (multi-select: finish)
 *   onCancel        : () => void
 *   selectLabel?    : "Select"            (single-select footer button text)
 *   maxSelected?    : number              (multi-select cap)
 *   testidPrefix?
 *   inline?         : boolean — render as an in-flow PAGE panel (fills its parent)
 *                     instead of a fixed-overlay modal. No portal, no backdrop, no
 *                     header close button. Used by the app-browse page (/apps).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X as XIcon, ChevronLeft, Loader2, Check, MoreVertical } from 'lucide-react';
import MarkdownRenderer from '../MarkdownRenderer.jsx';
import PortalMenu from '../PortalMenu.jsx';

function entryId(e) { return String(e?.id ?? e?.name ?? '').trim(); }

// Right-aligned per-tile actions dropdown (e.g. Review-tab Archive/Delete).
// Rendered inside the tile's shared top-right corner (see the tile map) so it
// isn't nested inside the tile <button>. Uses PortalMenu (never clipped).
export function TileActionsMenu({ actions, testid }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  if (!actions || !actions.length) return null;
  return (
    <div>
      <button
        type="button"
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="Actions"
        data-testid={testid}
        className="inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-200/70 hover:text-gray-700"
      >
        <MoreVertical className="h-4 w-4" aria-hidden />
      </button>
      <PortalMenu anchorRef={btnRef} open={open} onClose={() => setOpen(false)}
        align="bottom-right" width={160}>
        <ul className="py-1" role="menu">
          {actions.map((a) => (
            <li key={a.label}>
              <button
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); setOpen(false); a.onClick?.(); }}
                data-testid={a.testid}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 ${
                  a.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'
                }`}
              >
                {a.label}
              </button>
            </li>
          ))}
        </ul>
      </PortalMenu>
    </div>
  );
}

export default function CatalogBrowser({
  open,
  title = 'Add',
  subtitle = '',
  entries = [],
  tabs = null,
  loadDetail = null,
  multiSelect = false,
  selectedIds = [],
  onSelect = null,
  onToggle = null,
  onDone = null,
  onCancel,
  selectLabel = 'Select',
  maxSelected = 12,
  testidPrefix = 'catalog',
  inline = false,
  // When set, a tile click delegates to this instead of the built-in master-detail
  // (left list + right panel). The caller then owns the detail surface entirely —
  // used by the app-browse page so the detail is a standalone "all about this
  // network" view, not a row in a network list.
  onOpenEntry = null,
  // Controlled search: when ``query`` is a string, the caller owns the search
  // value (e.g. the app-browse page renders the input in its own tab bar) and
  // ``hideSearchBar`` suppresses the built-in search row.
  query = null,
  onQueryChange = null,
  hideSearchBar = false,
  // When true, the grid shows a spinner instead of the "Nothing matches" empty
  // state — the caller is still fetching, so absence of tiles ≠ no results.
  loading = false,
  // Optional per-tile actions: (entry) => [{ label, onClick, testid, danger? }].
  // Rendered as a right-aligned "⋮" dropdown on each tile. Empty/absent → no menu.
  tileActions = null,
  // Optional per-tile control rendered in the tile's top-right corner, LEFT of the
  // "⋮" actions menu: (entry) => ReactNode | null. Used for the version selector on
  // registry tiles. Must stopPropagation on its own clicks (it sits over the tile).
  tileControl = null,
  // Optional per-tile status tone: (entry) => 'green' | 'yellow' | null. Tints the
  // tile border to signal config completeness (registry Networks tab). Null = neutral.
  tileTone = null,
  // Optional leading visual per tile: (entry) => ReactNode | null. Rendered before
  // the label (grid + list + detail header). Used to show the network's identity
  // avatar (hexagon for registry packages). Generic — the host owns the visual.
  renderLeading = null,
}) {
  const controlledQ = query != null;
  const [internalQ, setInternalQ] = useState('');
  const q = controlledQ ? query : internalQ;
  const setQ = controlledQ ? (onQueryChange || (() => {})) : setInternalQ;
  const [tab, setTab] = useState(tabs?.[0]?.id || null);
  const [focused, setFocused] = useState(null); // entry being read (detail view)
  const [detail, setDetail] = useState(null);   // { loading, body, error }

  // Reset transient state when opened/closed. Only the internal query is reset
  // here; a controlled query is the caller's to clear.
  useEffect(() => { if (!open) { setInternalQ(''); setFocused(null); setDetail(null); } }, [open]);

  const selectedSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return entries.filter((e) => {
      if (tabs && tab && String(e.category || '') !== tab) return false;
      if (!needle) return true;
      return [e.label, e.description, e.id, e.name]
        .some((v) => String(v || '').toLowerCase().includes(needle));
    });
  }, [entries, q, tab, tabs]);

  // Open the detail view for an entry; lazy-load its body.
  const openDetail = useCallback(async (entry) => {
    setFocused(entry);
    if (!loadDetail) { setDetail({ body: entry.description || '' }); return; }
    setDetail({ loading: true });
    try {
      const d = await loadDetail(entry);
      // ``node`` (a custom React element) takes precedence over ``body`` markdown —
      // used for rich detail panels (e.g. the package view: diagram + agents + skills).
      setDetail({ node: d?.node || null, body: (d && d.body) || entry.description || '' });
    } catch (e) {
      setDetail({ body: entry.description || '', error: e?.message || '' });
    }
  }, [loadDetail]);

  const atCap = multiSelect && selectedSet.size >= maxSelected;

  const toggle = useCallback((entry) => {
    const id = entryId(entry);
    const now = !selectedSet.has(id);
    if (now && atCap) return;
    onToggle?.(entry, now);
  }, [selectedSet, atCap, onToggle]);

  const commitSingle = useCallback(() => {
    if (focused && onSelect) onSelect(focused);
  }, [focused, onSelect]);

  if (!open) return null;

  const selectedEntries = entries.filter((e) => selectedSet.has(entryId(e)));

  const panel = (
      <div className={
        inline
          ? 'flex h-full min-h-0 w-full flex-col overflow-hidden bg-white'
          : 'flex h-[88vh] max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl'
      }>
        {/* Header — modal only. Inline (page) mode owns its own header/tab bar,
            and the count moves to the footer status line to save vertical space. */}
        {!inline ? (
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <div>
              <h2 id={`${testidPrefix}-title`} className="text-base font-semibold text-gray-900">{title}</h2>
              {subtitle ? <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p> : null}
            </div>
            <button type="button" onClick={onCancel} aria-label="Close"
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        {/* Tabs + search (hidden in detail view to keep focus on reading).
            The whole row collapses when there are no tabs AND the search bar is
            owned by the caller (hideSearchBar) — saves vertical space. */}
        {!focused && (!!(tabs && tabs.length) || !hideSearchBar) ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5">
            <div className="flex items-center gap-1">
              {(tabs || []).map((t) => (
                <button key={t.id} type="button" onClick={() => { setTab(t.id); setQ(''); }}
                  className={`-mb-px border-b-2 px-2.5 py-2.5 text-xs font-medium ${
                    tab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
            {!hideSearchBar ? (
              <div className="relative my-2">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden />
                <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search…" data-testid={`${testidPrefix}-search`}
                  className="w-56 rounded-md border border-gray-200 py-1.5 pl-7 pr-2 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200" />
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Body: GRID (discover) or SPLIT (list + detail) */}
        {!focused ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-xs text-gray-400" data-testid={`${testidPrefix}-loading`}>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
              </div>
            ) : visible.length === 0 ? (
              <p className="py-8 text-center text-xs text-gray-400">Nothing matches “{q}”.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {visible.map((e) => {
                  const id = entryId(e);
                  const sel = selectedSet.has(id);
                  const actions = tileActions ? tileActions(e) : null;
                  const control = tileControl ? tileControl(e) : null;
                  const tone = tileTone ? tileTone(e) : null;
                  // Selection wins; else a status tone (green/yellow) tints the border;
                  // else the neutral default.
                  const toneCls = sel
                    ? 'border-emerald-400 bg-emerald-50/60'
                    : tone === 'yellow'
                      ? 'border-amber-300 bg-amber-50/50 hover:border-amber-400'
                      : tone === 'green'
                        ? 'border-emerald-300 bg-emerald-50/40 hover:border-emerald-400'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40';
                  return (
                    <div key={id} className="relative h-full">
                      <button type="button"
                        onClick={() => (onOpenEntry ? onOpenEntry(e) : openDetail(e))}
                        data-testid={`${testidPrefix}-tile-${id}`}
                        // min-h keeps every tile the height of a full 4-line
                        // description tile, so short ones (e.g. "Build new") don't
                        // look stunted next to released apps.
                        className={`flex h-full w-full min-h-[8.5rem] flex-col rounded-xl border p-3 text-left transition-colors ${toneCls}`}>
                        {sel ? <Check className="absolute right-2 top-2 h-4 w-4 text-emerald-600" aria-hidden /> : null}
                        {/* Reserve room on the right for the control + ⋮ menu. */}
                        <span className="flex items-start gap-2 pr-14">
                          {renderLeading ? renderLeading(e) : null}
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-gray-900">{e.label || id}</span>
                            {e.badge ? <span className="mt-0.5 block text-[10px] text-gray-400">{e.badge}</span> : null}
                          </span>
                        </span>
                        <span className="mt-1 line-clamp-4 text-[11px] text-gray-600">{e.description}</span>
                      </button>
                      {/* Shared top-right corner: version control (left) + ⋮ actions. */}
                      <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
                        {control}
                        <TileActionsMenu actions={actions} testid={`${testidPrefix}-tile-actions-${id}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            {/* Left 1×N list */}
            <div className="w-56 shrink-0 overflow-y-auto border-r border-gray-100 py-2">
              <button type="button" onClick={() => { setFocused(null); setDetail(null); }}
                className="mb-1 flex items-center gap-1 px-3 py-1 text-[11px] font-medium text-blue-600 hover:underline">
                <ChevronLeft className="h-3 w-3" /> All
              </button>
              {visible.map((e) => {
                const id = entryId(e);
                const isFocus = entryId(focused) === id;
                const sel = selectedSet.has(id);
                return (
                  <button key={id} type="button" onClick={() => openDetail(e)}
                    data-testid={`${testidPrefix}-list-${id}`}
                    className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[11px] ${
                      isFocus ? 'bg-blue-50 font-medium text-blue-800' : 'text-gray-700 hover:bg-gray-50'
                    }`}>
                    {renderLeading ? renderLeading(e) : null}
                    <span className="truncate">{e.label || id}</span>
                    {sel ? <Check className="ml-auto h-3 w-3 shrink-0 text-emerald-600" aria-hidden /> : null}
                  </button>
                );
              })}
            </div>
            {/* Right detail panel */}
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
                {renderLeading ? renderLeading(focused) : null}
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">{focused.label || entryId(focused)}</h3>
                  {focused.badge ? <span className="text-[10px] text-gray-400">{focused.badge}</span> : null}
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto text-xs text-gray-800">
                {detail?.loading ? (
                  <div className="flex items-center gap-2 px-5 py-3 text-gray-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
                ) : detail?.node ? (
                  detail.node
                ) : detail?.body ? (
                  <div className="px-5 py-3"><MarkdownRenderer content={detail.body} /></div>
                ) : (
                  <p className="px-5 py-3 text-gray-500">{focused.description || 'No description available.'}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex shrink-0 items-center gap-3 border-t border-gray-100 px-5 py-3">
          {multiSelect ? (
            <>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                {selectedEntries.length === 0 ? (
                  <span className="text-[11px] text-gray-400">No items selected.</span>
                ) : selectedEntries.map((e) => (
                  <span key={entryId(e)} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-900">
                    {e.label || entryId(e)}
                    <button type="button" onClick={() => onToggle?.(e, false)} aria-label={`Remove ${e.label}`}
                      className="text-emerald-700 hover:text-emerald-900">
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              {/* In detail view, the primary action toggles the focused entry. */}
              {focused ? (
                <button type="button" onClick={() => toggle(focused)}
                  disabled={!selectedSet.has(entryId(focused)) && atCap}
                  data-testid={`${testidPrefix}-toggle`}
                  className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                  {selectedSet.has(entryId(focused)) ? 'Remove' : (atCap ? `Max ${maxSelected}` : 'Add')}
                </button>
              ) : null}
              <button type="button" onClick={() => onDone?.()} data-testid={`${testidPrefix}-done`}
                className="shrink-0 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700">
                Done{selectedEntries.length ? ` (${selectedEntries.length})` : ''}
              </button>
            </>
          ) : (
            <>
              <span className="min-w-0 flex-1 truncate text-[11px] text-gray-400">
                {focused
                  ? `Reviewing ${focused.label || entryId(focused)}`
                  : (inline && subtitle ? subtitle : 'Pick an item to read its details.')}
              </span>
              <button type="button" onClick={commitSingle} disabled={!focused}
                data-testid={`${testidPrefix}-select`}
                className="shrink-0 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40">
                {selectLabel}
              </button>
            </>
          )}
        </div>
      </div>
  );

  // Inline: render in-flow (the page owns layout). Modal: fixed overlay + portal.
  if (inline) return panel;

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" aria-labelledby={`${testidPrefix}-title`}
      data-testid={`${testidPrefix}-dialog`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      {panel}
    </div>,
    document.body,
  );
}
